import React from "react";
import { motion } from "framer-motion";
import { MapPin, Target } from "lucide-react";
import {
  CLUBS_IN_NAIROBI_PERCENT,
  NAIROBI_GDP_PPP_RANGE,
  NAIROBI_POPULATION,
  OUTSIDE_NAIROBI_GDP_PPP_RANGE,
  OUTSIDE_NAIROBI_POPULATION,
  OUTSIDE_NAIROBI_POPULATION_PERCENT,
} from "../../constants/siteContent";
import { fadeInUp, staggerContainer } from "../../lib/animations";

export const ChallengeSection = () => {
  return (
    <section id="challenge" data-testid="challenge-section" className="py-24 md:py-28 bg-[#0A0A0A]">
      <div className="max-w-7xl mx-auto px-6">
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={staggerContainer}>
          <motion.div variants={fadeInUp} className="text-center mb-16 md:mb-[4.5rem]">
            <p className="text-[#D1111A] font-semibold tracking-[0.2em] uppercase mb-3">The Challenge</p>
            <h2 className="font-editorial text-4xl md:text-5xl text-white italic">Geographic Distribution</h2>
          </motion.div>

          <div className="grid lg:grid-cols-2 gap-8 md:gap-10 items-stretch max-w-5xl mx-auto">
            <motion.div variants={fadeInUp} className="space-y-8">
              <div className="bg-white/5 p-6 md:p-7 border-l-4 border-[#D1111A] h-full">
                <div className="flex items-center gap-4 mb-4">
                  <MapPin className="w-8 h-8 text-[#D1111A]" />
                  <span className="font-editorial text-xl text-white">Current State</span>
                </div>
                <div className="space-y-4">
                  <div>
                    <div className="flex justify-between text-white/80 mb-2">
                      <span>Clubs in Nairobi</span>
                      <span className="font-bold">{CLUBS_IN_NAIROBI_PERCENT}</span>
                    </div>
                    <div className="h-3 bg-white/20 rounded-full overflow-hidden">
                      <motion.div className="h-full bg-[#D1111A] rounded-full" initial={{ width: 0 }} whileInView={{ width: "67%" }} viewport={{ once: true }} transition={{ duration: 1.5, ease: "easeOut" }} />
                    </div>
                  </div>
                  <div>
                    <div className="flex justify-between text-white/80 mb-2">
                      <span>Population outside Nairobi</span>
                      <span className="font-bold whitespace-nowrap">{OUTSIDE_NAIROBI_POPULATION} · {OUTSIDE_NAIROBI_POPULATION_PERCENT}</span>
                    </div>
                    <div className="h-3 bg-white/20 rounded-full overflow-hidden">
                      <motion.div className="h-full bg-white rounded-full" initial={{ width: 0 }} whileInView={{ width: "87%" }} viewport={{ once: true }} transition={{ duration: 1.5, ease: "easeOut", delay: 0.3 }} />
                    </div>
                  </div>
                  <div>
                    <div className="flex justify-between text-white/80 mb-2 gap-4">
                      <span>GDP PPP outside Nairobi</span>
                      <span className="font-bold whitespace-nowrap">{OUTSIDE_NAIROBI_GDP_PPP_RANGE}</span>
                    </div>
                    <div className="h-3 bg-white/20 rounded-full overflow-hidden">
                      <motion.div className="h-full bg-white/70 rounded-full" initial={{ width: 0 }} whileInView={{ width: "100%" }} viewport={{ once: true }} transition={{ duration: 1.5, ease: "easeOut", delay: 0.45 }} />
                    </div>
                  </div>
                </div>
                <p className="mt-5 text-sm leading-relaxed text-white/72" data-testid="challenge-gdp-ppp-note">
                  Using Nairobi County at approximately {NAIROBI_POPULATION} people and {NAIROBI_GDP_PPP_RANGE} GDP PPP, the remaining population and GDP PPP outside Nairobi are approximately {OUTSIDE_NAIROBI_POPULATION} and {OUTSIDE_NAIROBI_GDP_PPP_RANGE}.
                </p>
              </div>
            </motion.div>

            <motion.div variants={fadeInUp} className="space-y-8">
              <div className="bg-[#D1111A]/20 p-6 md:p-7 border-l-4 border-white h-full">
                <div className="flex items-center gap-4 mb-4">
                  <Target className="w-8 h-8 text-white" />
                  <span className="font-editorial text-xl text-white">Our Goal</span>
                </div>
                <p className="text-white/80 leading-relaxed">
                  Get more teams <strong className="text-white">incorporated and/or operating outside of Nairobi</strong> to promote football and youth empowerment in line with the 2010 Constitution.
                </p>
              </div>
            </motion.div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};